import React from 'react';
import { CloseUniversal, EditableOutlined } from '@duxton-web/icons';
import { Divider, Flex, Popover, Tag } from '@duxton-web/ui';

import AppVersionPrompt from './AppVersionPrompt';
import {
  getAppOptionsMap,
  osOptionsMap,
  parseAppVersion,
} from '@/common/AppVersionInput/appVersionUtils';
import { getTenant } from '@/common/Tenants';

interface AppVersionTagProps {
  value?: string;
  disabled?: boolean;
  showAppVersionPrompt?: boolean;
  onChange?: (val: string, prev: string) => void;
  onRemove?: (val: string) => void;
  onShowPopover?: (val: string) => void;
}

function EditTag({ disabled, onClick }) {
  if (disabled) return <></>;

  return (
    <EditableOutlined
      data-testid="app-version-tag-edit"
      style={{
        cursor: 'pointer',
      }}
      onClick={onClick}
    />
  );
}

function RemoveTag({
  disabled,
  onClick,
}: {
  disabled: boolean;
  onClick: () => void;
}) {
  if (disabled) return <></>;

  return (
    <CloseUniversal
      data-testid="app-version-tag-remove"
      style={{
        cursor: 'pointer',
      }}
      onClick={onClick}
    />
  );
}

export default function AppVersionTag({
  value,
  disabled,
  showAppVersionPrompt,
  onChange = () => {},
  onRemove = () => {},
  onShowPopover = () => {},
}: AppVersionTagProps) {
  const [app, os, version] = parseAppVersion(value);
  const appOptionsMap = getAppOptionsMap(getTenant());

  return (
    <Popover
      placement="bottomLeft"
      open={showAppVersionPrompt}
      content={
        <AppVersionPrompt
          value={value}
          onSubmit={(val: string, prev: string) => {
            onChange(val, prev);

            onShowPopover('');
          }}
          onCancel={() => {
            onShowPopover('');
          }}
        />
      }
      trigger="click"
      onOpenChange={(open: boolean) => {
        if (open) onShowPopover(value);
        else onShowPopover('');
      }}
      destroyTooltipOnHide={true}
    >
      <Tag
        data-testid="app-version-tag"
        bordered={false}
        style={{ marginRight: 0 }}
      >
        <Flex align="center">
          {`${appOptionsMap[app]}-${osOptionsMap[os]}-${version}`}
          <Divider type="vertical" />
          <Flex align="center" gap={4}>
            <EditTag
              disabled={disabled}
              onClick={() => {
                onShowPopover(value);
              }}
            />
            <RemoveTag
              disabled={disabled}
              onClick={() => {
                onRemove(value);

                onShowPopover('');
              }}
            />
          </Flex>
        </Flex>
      </Tag>
    </Popover>
  );
}
